import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router";
import { cultDetails as bio } from "../assets/componentDetails";
import { cultTabs } from "../assets/pageTabs";
import CultMembers from "../components/CultMembers";
import GridController from "../components/GridController";
import Header from "../components/Header";
import Team from "../components/Team";
import usePageReset from "../hooks/usePageReset";
import usePathname from "../hooks/usePathname";
import { Departments } from "../schema";
import structuredData from "../assets/structuredText";

export default function Cult() {
  const { department } = useParams();
  const [displayed, setDisplayed] = useState<string>(
    (department as string) || Departments.principals
  );

  const chooseTab = useCallback(
    (id: string) => {
      setDisplayed(id);
    },
    [setDisplayed]
  );

  const { page } = usePathname();

  usePageReset();

  const navigate = useNavigate();

  useEffect(() => {
    if (page === "join") {
      setDisplayed(Departments.cultivators);
    } else {
      const departmentFound = Object.keys(Departments).includes(department as string);
      if (departmentFound) setDisplayed(department as string);
      if (!departmentFound && department) navigate(`/404`);
    }
  }, [department]);

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta
          name="keywords"
          content="people team principals entrepreneurs innovators cultiv cultivators"
        />
        <meta
          name="description"
          content="Join Cultiv’s bold community of cultivators, mentors, innovators and thought
leaders, where you can make an impact and shape the future."
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:url" content="https://cultiv.llc" />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          itemProp="image"
          content="https://cultiv.llc/images/metaimg.jpg"
        />
        <link rel="image_src" itemProp="image" href="https://cultiv.llc/images/metaimg.jpg" />
        <meta
          property="og:description"
          content="Join Cultiv’s bold community of cultivators, mentors, innovators and thought
leaders, where you can make an impact and shape the future."
        />
        <meta property="og:site_name" content="Cultiv LLC" />
        <meta property="og:title" content="Cultiv | Cult" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://cultiv.llc" />
        <meta property="twitter:title" content="Cultiv | Cult" />
        <meta
          property="twitter:description"
          content="Join Cultiv’s bold community of cultivators, mentors, innovators and thought
leaders, where you can make an impact and shape the future."
        />
        <meta property="twitter:image" content="https://cultiv.llc/images/metaimg.jpg" />
        <link rel="canonical" href="https://cultiv.llc" />
        <script type="application/ld+json">{structuredData}</script>
        <title>Cult</title>
      </Helmet>
      <div className="w-full flex flex-col justify-center items-center lg:gap-10 gap-6 font-Light">
        <Header desc={bio.text} color="bg-cultiv-blue" textColor="white" />
        <div className="flex flex-col lg:gap-10 gap-4 justify-center items-center w-full  md:px-15 lg:px-20 xl:px-30 px-6">
          <GridController
            displayed={displayed}
            setDisplayed={chooseTab}
            tabs={cultTabs}
            page="cult"
          />
          {displayed === "team" ? (
            <Team displayed={displayed} />
          ) : (
            <CultMembers displayed={displayed} />
          )}
        </div>
      </div>
    </>
  );
}
